import { getCurrentDateLocale } from '@longy/common'
import { Skeleton } from '@longy/web-components'
import { createFileRoute } from '@tanstack/react-router'
import { formatInTimeZone } from 'date-fns-tz'
import { CalendarClock, User } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { Footer, Header } from '../../components/landing'
import { WebinarViewer } from '../../components/webinar/WebinarViewer'
import { api, isTRPCClientError } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'
import { deriveWebinarJoinView } from '../../utils/webinarJoin'

const WEBINAR_TIMEZONE = 'Europe/Budapest'

const liveKitUrl = import.meta.env.VITE_LIVEKIT_URL as string | undefined

const joinButtonClassName =
    'rounded-full bg-primary px-8 py-3 font-bold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/40'

const noticeClassName = 'rounded-2xl bg-slate-100 px-6 py-4 text-sm font-semibold text-brand-gray'

const formatWebinarTime = (date: Date, pattern: string) =>
    formatInTimeZone(date, WEBINAR_TIMEZONE, pattern, { locale: getCurrentDateLocale() })

const WebinarJoinPage = () => {
    const { token } = Route.useParams()
    const { t } = useTranslation()
    const { getLocalizedValue } = useLocale()
    const [liveKitToken, setLiveKitToken] = useState<string | null>(null)
    const [hasJoinError, setHasJoinError] = useState(false)

    const { data, isLoading, error } = api.video.getWebinarByJoinToken.useQuery(
        { joinToken: token },
        { retry: false },
    )

    const { mutateAsync: requestLiveKitToken, isPending } =
        api.video.getWebinarTokenByJoinToken.useMutation()

    const isInvalidLink = isTRPCClientError(error) && error.data?.code === 'NOT_FOUND'
    const hasUnexpectedError = !!error && !isInvalidLink && !liveKitToken

    const view = deriveWebinarJoinView({
        isLoading,
        isError: isInvalidLink,
        status: data?.status,
        canJoin: data?.canJoin,
        hasLiveKitToken: !!liveKitToken,
    })

    const handleJoin = async () => {
        setHasJoinError(false)
        try {
            const { token: accessToken } = await requestLiveKitToken({ joinToken: token })
            setLiveKitToken(accessToken)
        } catch {
            setHasJoinError(true)
        }
    }

    const body = match(view)
        .with('loading', () => (
            <div className="space-y-4">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-12 w-56 rounded-full" />
            </div>
        ))
        .with('invalid', () => (
            <p className={noticeClassName}>{t('frontend.webinar.invalidLink')}</p>
        ))
        .with('ended', () => <p className={noticeClassName}>{t('frontend.webinar.ended')}</p>)
        .with('upcoming', () => (
            <div className="space-y-4">
                <button disabled type="button" className={joinButtonClassName}>
                    {t('frontend.webinar.join')}
                </button>
                <p className={noticeClassName}>{t('frontend.webinar.opensBeforeStart')}</p>
            </div>
        ))
        .with('ready', () => (
            <button
                type="button"
                className={joinButtonClassName}
                disabled={isPending}
                onClick={handleJoin}
            >
                {t('frontend.webinar.join')}
            </button>
        ))
        .with('joined', () =>
            liveKitUrl ? (
                <div className="space-y-4">
                    <WebinarViewer serverUrl={liveKitUrl} token={liveKitToken ?? ''} />
                    <button
                        type="button"
                        className="rounded-full border border-slate-200 px-8 py-3 font-bold text-brand-dark transition-colors hover:bg-slate-50"
                        onClick={() => setLiveKitToken(null)}
                    >
                        {t('frontend.webinar.leave')}
                    </button>
                </div>
            ) : (
                <p className={noticeClassName}>{t('frontend.webinar.joinFailed')}</p>
            ),
        )
        .exhaustive()

    return (
        <div className="min-h-screen bg-off-white font-sans text-brand-dark">
            <Header />
            <main className="flex flex-col">
                <section className="bg-white py-12">
                    <div className="mx-auto w-full max-w-[960px] px-6">
                        {data && (
                            <div className="mb-8">
                                <span className="mb-4 inline-block rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white">
                                    {t('frontend.events.webinarBadge')}
                                </span>
                                <h1 className="mb-6 text-3xl font-bold text-brand-dark md:text-4xl">
                                    {getLocalizedValue(data.name)}
                                </h1>
                                <div className="flex flex-col gap-3 text-sm text-brand-gray sm:flex-row sm:flex-wrap sm:gap-6">
                                    <div className="flex items-center gap-2">
                                        <CalendarClock className="h-5 w-5 text-primary" />
                                        <span>
                                            {formatWebinarTime(
                                                data.startDate,
                                                'yyyy. MMMM d. HH:mm',
                                            )}
                                            {' – '}
                                            {formatWebinarTime(data.endDate, 'HH:mm')}
                                        </span>
                                    </div>
                                    {data.attendeeName && (
                                        <div className="flex items-center gap-2">
                                            <User className="h-5 w-5 text-primary" />
                                            <span>
                                                {t('frontend.webinar.attendee')}:{' '}
                                                {data.attendeeName}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {hasUnexpectedError ? (
                            <p className={noticeClassName}>{t('common.errors.defaultError')}</p>
                        ) : (
                            body
                        )}

                        {hasJoinError && (
                            <p className="mt-4 text-sm font-medium text-red-500">
                                {t('frontend.webinar.joinFailed')}
                            </p>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export const Route = createFileRoute('/webinar/join/$token')({
    component: WebinarJoinPage,
    head: () => ({
        meta: [{ title: 'Long-Y - Webinár' }],
    }),
})
